import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";

import Layout from "./components/Layout";
import Login from "./features/auth/Login";
import LegalPage from "./legal/LegalPage";
import RequireAuth from "./features/auth/RequireAuth";
import RequireApproved from "./features/auth/RequireApproved";
import UsersList from "./features/users/UsersList";
import CustomerPersonasList from "./features/customer_personas/CustomerPersonasList";
import CustomerPersonaPage from "./features/customer_personas/CustomerPersonaPage";
import ChatSessionsList from "./features/chat_sessions/ChatSessionsList";
import ChatSessionDetail from "./features/chat_sessions/ChatSessionDetail"; // Import kept if needed elsewhere, but likely replaced by Report
import ChatSessionPerform from "./features/chat_sessions/ChatSessionPerform";
import ChatSessionReport from "./features/chat_sessions/ChatSessionReport"; // Import the new component
import CustomerPersonaCards from "./features/customer_personas/CustomerPersonaCards";
import SessionModeSelect from "./features/chat_sessions/SessionModeSelect";
import PracticePersonaSelect from "./features/chat_sessions/PracticePersonaSelect";


import EvaluationSessionsList from "./features/evaluation_sessions/EvaluationSessionsList";
import EvaluationDetail from "./features/evaluation_sessions/EvaluationSessionDetail";
import PartnerEvaluatedSessionList from "./features/partner_evaluated_sessions/PartnerEvaluatedSessionList";
import Playground from "./features/bench/Playground";
import LandingPage from "./components/LandingPage";
import EvaluationCriteriaGroupList from "./features/evaluation_criteria_group/EvaluationCriteriaGroupList";
import EvaluationCriteriaGroupPage from "./features/evaluation_criteria_group/EvaluationCriteriaGroupPage";
import FeedbackSessionsList from "./features/feedback/FeedbackList";
import FeedbackPerform from "./features/feedback/FeedbackPerform";
import AudioOrb from "./features/AudioOrb";
import AudioOrbOpenAI from "./features/AudioOrbOpenAI";
import V2ChatSessionPerform from "./features/v2/chat_sessions/v2ChatSessionPerform";
import V2ChatSessionList from "./features/v2/chat_sessions/v2ChatSessionList";
import UsageDashboardPage from "./features/usage_dashboard/UsageDashboardPage";
import UserDashboardPage from "./features/user_dashboard/UserDashboardPage";
import UploadDoc from './features/upload_doc/Upload'
import AlignmentAnalytics from "./features/analytics/AlignmentAnalytics";
import PKSalesAnalytics from "./features/analytics/PKSalesAnalytics";
import RequireAdmin from "./features/auth/RequireAdmin";
import RequireSuperuser from "./features/auth/RequireSuperuser";
import ProductManagement from "./features/product_knowledge/ProductManagement";
import ProductDetails from "./features/product_knowledge/ProductDetails";
import QuizQuestionManagement from "./features/product_knowledge/QuizQuestionManagement";
import QuizQuestionDetails from "./features/product_knowledge/QuizQuestionDetails";
import QuizDashboard from "./features/quiz/QuizDashboard";
import QuizSession from "./features/quiz/QuizSession";
import QuizAttemptsList from "./features/quiz/QuizAttemptsList";
import QuizAttemptDetail from "./features/quiz/QuizAttemptDetail";
import ContractorManagement from "./features/contractors/ContractorManagement";
import ContractorDetail from "./features/contractors/ContractorDetail";
import OnboardingPage from "./features/onboarding/OnboardingPage";
import PendingApprovalPage from "./features/onboarding/PendingApprovalPage";
import MySupportTicketsPage from "./features/support_tickets/MySupportTicketsPage";
import SupportTicketDetailPage from "./features/support_tickets/SupportTicketDetailPage";
import AdminSupportTicketsPage from "./features/support_tickets/AdminSupportTicketsPage";
import AdminSupportTicketDetailPage from "./features/support_tickets/AdminSupportTicketDetailPage";

const queryClient = new QueryClient();

const RedirectKeepingQuery = ({ to }) => <Navigate to={`${to}${useLocation().search}`} replace />;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* public routes */}
          <Route path="login" element={<Login />} />
          <Route path="terms" element={<LegalPage doc="terms" />} />
          <Route path="privacy" element={<LegalPage doc="privacy" />} />

          {/* protected routes */}
          <Route element={<RequireAuth />}>
            {/* Onboarding routes — accessible before approval */}
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="pending-approval" element={<PendingApprovalPage />} />
            {/* Approved-only routes */}
            <Route element={<RequireApproved />}>
            <Route index element={<LandingPage />} />
            <Route path="playground" element={<Playground />} />
            <Route path="landing" element={<LandingPage />} />
            <Route path="userslist" element={<UsersList />} />
            <Route path="customerpersonaslist" element={<CustomerPersonasList />} />
            <Route path="customerpersonas/:id" element={<CustomerPersonaPage />} />
            <Route path="simulation" element={<SessionModeSelect />} />
            <Route path="simulation/practice" element={<PracticePersonaSelect />} />
            <Route path="customerpersonascards" element={<CustomerPersonaCards />} />
            <Route path="chatsessionslist" element={<ChatSessionsList />} />
            <Route path="chatsessions/:id" element={<ChatSessionReport />} />
            <Route path="chatsessionperform" element={<ChatSessionPerform />} />
            <Route path="evaluationsessionslist" element={<EvaluationSessionsList />} />
            <Route path="evaluationsessiondetail" element={<EvaluationDetail />} />
            <Route path="evaluationcriteriagrouplist" element={<EvaluationCriteriaGroupList />} />
            <Route path="evaluationcriteriagroups/:id" element={<EvaluationCriteriaGroupPage />} />
            <Route path="feedbackslist" element={<FeedbackSessionsList />} />
            <Route path="feedbackperform" element={<FeedbackPerform />} />
            <Route path="partnerevaluatedsessionlist" element={<PartnerEvaluatedSessionList />} />
            <Route path="chatsessionperform-v2" element={<V2ChatSessionPerform />} />
            <Route path="chatsessionslist-v2" element={<V2ChatSessionList />} />
            <Route path="usage" element={<UsageDashboardPage />} />
            <Route path="userdashboard" element={<UserDashboardPage />} />
            <Route path="alignment-study" element={<AlignmentAnalytics />} />
            <Route path="pk-alignment-study" element={<PKSalesAnalytics />} />
            {/* <Route path="upload" element={<UploadDoc/>} /> */}
            <Route path="audio-orb" element={<AudioOrb />} />
            <Route path="audio-orb-openai" element={<AudioOrbOpenAI />} />

            {/* Quiz Routes */}
            <Route path="quiz/dashboard" element={<QuizDashboard />} />
            <Route path="quiz/session" element={<QuizSession />} />
            {/* 퀴즈 내역: everyone's own attempts; admins see their scope (API-enforced). */}
            <Route path="quiz/attempts" element={<QuizAttemptsList />} />
            <Route path="quiz/attempts/:id" element={<QuizAttemptDetail />} />

            <Route path="my-feedback" element={<MySupportTicketsPage />} />
            <Route path="my-feedback/:id" element={<SupportTicketDetailPage />} />

            <Route element={<RequireSuperuser />}>
              <Route path="admin/support-tickets" element={<AdminSupportTicketsPage />} />
              <Route path="admin/support-tickets/:id" element={<AdminSupportTicketDetailPage />} />
            </Route>

            {/* Admin Routes */}
            <Route element={<RequireAdmin />}>
                <Route path="admin/contractors" element={<ContractorManagement />} />
                <Route path="admin/contractors/:id" element={<ContractorDetail />} />
                <Route path="admin/products" element={<ProductManagement />} />
                <Route path="admin/products/:id" element={<ProductDetails />} />
                <Route path="admin/quiz-questions" element={<QuizQuestionManagement />} />
                <Route path="admin/quiz-questions/:id" element={<QuizQuestionDetails />} />
                {/* Moved to /quiz/attempts when trainees got the page too; old links keep working. */}
                <Route path="admin/quiz-attempts" element={<RedirectKeepingQuery to="/quiz/attempts" />} />
            </Route>
            </Route>

          </Route>
        </Route>
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
